import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type HubInfo, type Job, type JobLog, type Relay } from '@/api/client';
import { ConfirmDialog } from './ConfirmDialog';
import { formatBytes, formatDate } from './format';

const POLL_MS = 3000;

/** The project's pipeline jobs on the hub: progress, log, cancel, delete, and the ingest
 *  command for a finished job (conversion stays on a workstation, see the plan). */
export function JobsPanel({
  projectId,
  hub,
  isAdmin,
  onRunsChanged,
}: {
  projectId: string;
  hub: HubInfo | null;
  isAdmin: boolean;
  /** A job reaching a terminal state may mean a run was ingested; the parent reloads. */
  onRunsChanged?: () => void;
}) {
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [relays, setRelays] = useState<Relay[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [openLog, setOpenLog] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Job | null>(null);
  const prevActive = useRef<Set<string>>(new Set());

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ jobs: Job[]; relays: Relay[] }>(`/api/projects/${projectId}/jobs`);
      setJobs(r.jobs);
      setRelays(r.relays ?? []);
      setError(null);
      const active = new Set(r.jobs.filter((j) => j.is_active).map((j) => j.id));
      const finished = [...prevActive.current].some((id) => !active.has(id));
      const linked = r.jobs.some((j) => j.run_id && !prevActive.current.has(j.id) && j.is_active === false);
      prevActive.current = active;
      if (finished || linked) onRunsChanged?.();
    } catch (e) {
      setError((e as Error).message);
    }
  }, [projectId, onRunsChanged]);

  useEffect(() => {
    void load();
  }, [load]);
  const anyRelaying = relays.length > 0;
  const anyActive = (jobs?.some((j) => j.is_active || j.ingest_state === 'running') ?? false);
  const anyUningested = jobs?.some((j) => j.state === 'succeeded' && !j.run_id) ?? false;
  useEffect(() => {
    if (!anyActive && !anyUningested && !anyRelaying) return;
    const t = setInterval(() => void load(), anyRelaying ? 1000 : anyActive ? POLL_MS : POLL_MS * 5);
    return () => clearInterval(t);
  }, [anyActive, anyUningested, anyRelaying, load]);

  async function cancel(job: Job) {
    try {
      await api.post(`/api/jobs/${job.id}/cancel`);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function ingestNow(job: Job) {
    try {
      await api.post(`/api/jobs/${job.id}/ingest`);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  if (jobs === null && !error) return null;
  if (jobs && jobs.length === 0 && relays.length === 0 && !error) return null;

  return (
    <section className="jobs">
      <h2>Pipeline jobs</h2>
      {error && <div className="error">{error}</div>}
      <div className="table-wrap">
      <table className="table jobs-table">
        <thead>
          <tr>
            <th>State</th>
            <th>Progress</th>
            <th>Input</th>
            <th>Pipeline</th>
            <th>Submitted</th>
            <th>Worker</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {relays.map((r) => (
            <tr key={r.upload_id} className="job state-relaying">
              <td data-label="State">
                <span className="badge state preparing">sending</span>
              </td>
              <td className="progress-cell" data-label="Progress">
                <div className="progress small">
                  <div className="bar" style={{ width: `${Math.min(100, r.percent)}%` }} />
                </div>
                <div className="muted small">
                  {r.percent.toFixed(0)}% · {formatBytes(r.sent)} of {formatBytes(r.total)} to the hub
                </div>
              </td>
              <td data-label="Input">
                <div className="mono small">{r.filename}</div>
              </td>
              <td colSpan={4} className="muted small span">
                becomes a job once the hub has the whole scan
              </td>
            </tr>
          ))}
          {jobs?.map((j) => (
            <JobRows
              key={j.id}
              job={j}
              hub={hub}
              isAdmin={isAdmin}
              logOpen={openLog === j.id}
              onToggleLog={() => setOpenLog(openLog === j.id ? null : j.id)}
              onCancel={() => void cancel(j)}
              onDelete={() => setConfirm(j)}
              onIngest={() => void ingestNow(j)}
            />
          ))}
        </tbody>
      </table>
      </div>
      {confirm && (
        <ConfirmDialog
          title="Delete job"
          onClose={() => setConfirm(null)}
          onConfirm={async () => {
            await api.del(`/api/jobs/${confirm.id}`);
            await load();
          }}
        >
          <p>
            Forget job <span className="mono">{confirm.id.slice(0, 12)}</span> and remove its scan, result and log from the hub.
            {confirm.run_id ? ' The run already ingested from it stays.' : ''}
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}

function JobRows({
  job: j,
  hub,
  isAdmin,
  logOpen,
  onToggleLog,
  onCancel,
  onDelete,
  onIngest,
}: {
  job: Job;
  hub: HubInfo | null;
  isAdmin: boolean;
  logOpen: boolean;
  onToggleLog: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onIngest: () => void;
}) {
  const pct = Math.max(0, Math.min(100, j.percent));
  return (
    <>
      <tr className={`job state-${j.state}`}>
        <td data-label="State">
          <span className={`badge state ${j.state}`}>{j.state}</span>
          {j.hub_gone && <span className="badge">hub record gone</span>}
        </td>
        <td className="progress-cell" data-label="Progress">
          {j.is_active ? (
            <>
              <div className="progress small">
                <div className="bar" style={{ width: `${pct}%` }} />
              </div>
              <div className="muted small">
                {pct.toFixed(0)}% · {j.stage ?? (j.state === 'queued' ? 'waiting for a worker' : j.state === 'preparing' ? 'worker preparing (bundle, scan)' : 'running')}
              </div>
            </>
          ) : j.state === 'succeeded' ? (
            j.run_id ? (
              <Link to={`/runs/${j.run_id}`} className="button">
                Open run
              </Link>
            ) : j.ingest_state === 'running' ? (
              <span className="small converting">
                <span className="pulse" /> converting to web assets…
              </span>
            ) : j.ingest_state === 'failed' ? (
              <span className="small error-text" title={j.ingest_error ?? ''}>
                conversion failed{j.ingest_error ? `: ${j.ingest_error.slice(0, 80)}` : ''}
              </span>
            ) : hub?.auto_ingest ? (
              <span className="muted small">finished · conversion queued</span>
            ) : (
              <span className="muted small">finished · awaiting ingest</span>
            )
          ) : (
            <span className="muted small" title={j.error ?? ''}>
              {j.error ? j.error.slice(0, 80) : j.exit_code !== null ? `exit ${j.exit_code}` : '-'}
            </span>
          )}
          <JobReports job={j} />
        </td>
        <td data-label="Input">
          <div className="mono small">{j.input_filename}</div>
          <div className="muted small">
            {j.input_bytes !== null ? formatBytes(j.input_bytes) : ''}
            {j.extra_args.length ? ` · ${j.extra_args.join(' ')}` : ''}
          </div>
        </td>
        <td data-label="Pipeline">
          <span className="badge">{j.version ?? j.pipeline}</span>
        </td>
        <td data-label="Submitted">
          <div className="small">{formatDate(j.created_at)}</div>
          <div className="muted small">{j.created_by ?? ''}</div>
        </td>
        <td className="small" data-label="Worker">
          {j.worker_name ?? <span className="muted">{j.pinned_worker ? `waiting for ${j.pinned_worker}` : '-'}</span>}
          {j.pinned_worker && <div className="muted small">pinned</div>}
        </td>
        <td className="actions">
          <button className="link" onClick={onToggleLog} disabled={j.hub_gone && !j.ingest_state}>
            {logOpen ? 'Hide log' : 'Log'}
          </button>
          {isAdmin && hub?.auto_ingest && j.state === 'succeeded' && j.result_available && j.ingest_state !== 'running' && (
            <button className="link" onClick={onIngest} title={j.run_id ? 'Convert the result again and replace the run' : 'Convert the result into a run now'}>
              {j.ingest_state === 'failed' ? 'Retry conversion' : j.run_id ? 'Re-convert' : 'Convert now'}
            </button>
          )}
          {isAdmin && j.is_active && !j.hub_gone && (
            <button className="link" onClick={onCancel}>
              Cancel
            </button>
          )}
          {isAdmin && (!j.is_active || j.hub_gone) && (
            <button className="link danger" onClick={onDelete}>
              Delete
            </button>
          )}
        </td>
      </tr>
      {j.state === 'succeeded' && !j.run_id && !j.hub_gone && isAdmin && !hub?.auto_ingest && (
        <tr className="job-detail">
          <td colSpan={7}>
            <IngestHint job={j} hub={hub} />
          </td>
        </tr>
      )}
      {logOpen && (
        <tr className="job-detail">
          <td colSpan={7}>
            {!j.hub_gone && <JobLogView jobId={j.id} active={j.is_active} />}
            {j.ingest_state && <IngestLogView jobId={j.id} active={j.ingest_state === 'running'} />}
          </td>
        </tr>
      )}
    </>
  );
}

/** The reports requested with the job: a download (through the hub) once rendered, the
 *  worker's error when it could not render one, pending while the job runs. The copy kept
 *  with the run (runs table) outlives the hub's retention; this one does not. */
function JobReports({ job }: { job: Job }) {
  const reports = job.reports ?? [];
  if (reports.length === 0) return null;
  return (
    <div className="report-links small">
      {reports.map((r) => {
        const label = r.display_name ?? r.name;
        if (r.available && !job.hub_gone) {
          return (
            <a key={r.name} href={`/api/jobs/${job.id}/reports/${r.name}`} target="_blank" rel="noreferrer" title={r.filename ?? ''}>
              {label}
            </a>
          );
        }
        if (r.error) {
          return (
            <span key={r.name} className="error-text" title={r.error}>
              {label}: not rendered
            </span>
          );
        }
        return (
          <span key={r.name} className="muted">
            {label}
            {job.is_active ? ' · after the run' : job.hub_gone ? ' · gone from the hub' : ''}
          </span>
        );
      })}
    </div>
  );
}

function IngestHint({ job, hub }: { job: Job; hub: HubInfo | null }) {
  const [copied, setCopied] = useState(false);
  const i = hub?.ingest;
  const cmd = [
    'valvia-web ingest',
    `--job ${job.id}`,
    i?.hub_bucket ? `--hub-bucket ${i.hub_bucket}` : '--hub-bucket <hub bucket>',
    i?.web_bucket ? `--bucket ${i.web_bucket}` : '--bucket <web bucket>',
    i?.region ? `--region ${i.region}` : '',
    `--api ${i?.api ?? window.location.origin}`,
    '--token <admin token>',
    '--keep-source',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className="ingest-hint">
      <div>
        The pipeline finished. Converting the export to web assets runs on a workstation with the ingest CLI; the run
        appears here once it registers.
      </div>
      <pre className="mono">{cmd}</pre>
      <button
        className="secondary"
        onClick={() => {
          void navigator.clipboard?.writeText(cmd).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
      >
        {copied ? 'Copied' : 'Copy command'}
      </button>
    </div>
  );
}

function IngestLogView({ jobId, active }: { jobId: string; active: boolean }) {
  const [text, setText] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const pre = useRef<HTMLPreElement>(null);
  useEffect(() => {
    let stopped = false;
    const fetchLog = async () => {
      try {
        const res = await fetch(`/api/jobs/${jobId}/ingest-log`, { credentials: 'same-origin' });
        if (res.status === 404) {
          if (!stopped) setText('');
          return;
        }
        if (!res.ok) throw new Error(res.statusText);
        const t = await res.text();
        if (!stopped) {
          setText(t);
          setError(null);
        }
      } catch (e) {
        if (!stopped) setError((e as Error).message);
      }
    };
    void fetchLog();
    if (!active) return () => { stopped = true; };
    const t = setInterval(() => void fetchLog(), 2000);
    return () => {
      stopped = true;
      clearInterval(t);
    };
  }, [jobId, active]);
  useEffect(() => {
    if (pre.current) pre.current.scrollTop = pre.current.scrollHeight;
  }, [text]);
  return (
    <div className="job-log">
      <div className="muted small">Conversion log (ingest on the server){active ? ' · live' : ''}</div>
      {error && <div className="error">{error}</div>}
      <pre ref={pre} className="log">
        {text || (active ? 'Starting…' : 'No conversion log.')}
      </pre>
    </div>
  );
}

function JobLogView({ jobId, active }: { jobId: string; active: boolean }) {
  const [lines, setLines] = useState<string[]>([]);
  const [cursor, setCursor] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [follow, setFollow] = useState(true);
  const pre = useRef<HTMLPreElement>(null);

  const fetchMore = useCallback(async () => {
    try {
      const chunk = await api.get<JobLog>(`/api/jobs/${jobId}/log?since=${cursor}`);
      if (chunk.lines.length) {
        setLines((prev) => prev.concat(chunk.lines));
        setCursor(chunk.next);
      }
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [jobId, cursor]);

  useEffect(() => {
    void fetchMore();
    if (!active) return;
    const t = setInterval(() => void fetchMore(), 2000);
    return () => clearInterval(t);
  }, [fetchMore, active]);

  useEffect(() => {
    if (follow && pre.current) pre.current.scrollTop = pre.current.scrollHeight;
  }, [lines, follow]);

  return (
    <div className="job-log">
      <div className="row">
        <span className="muted small">
          {lines.length} line{lines.length === 1 ? '' : 's'}
          {active ? ' · live' : ''}
        </span>
        <span className="spacer" />
        <label className="check small">
          <input type="checkbox" checked={follow} onChange={(e) => setFollow(e.target.checked)} /> Follow
        </label>
      </div>
      {error && <div className="error">{error}</div>}
      <pre
        ref={pre}
        className="log"
        onScroll={(e) => {
          const el = e.currentTarget;
          setFollow(el.scrollTop + el.clientHeight >= el.scrollHeight - 8);
        }}
      >
        {lines.length ? lines.join('\n') : active ? 'Waiting for output…' : 'No log lines.'}
      </pre>
    </div>
  );
}
